// src/api/authApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setUser, setIsKeepLogin } from "../reducers/auth-reducer";
import { getAccessToken } from "../../utils";
import { saveCodeLogin } from "../../utils/codeLogin";
import {
  EmailChangeConfirmResponse,
  EmailChangeStartResponse,
  LoginCodeRequestResponse,
  LoginCodeVerifyPayload,
  LoginCodeVerifyResponse,
  LoginMethodResponse,
} from "../../types/code-login-types";

const BASE_URL = `${process.env.REACT_APP_API_URL}api/users`;

// Email-first login calls are made before sign-in: never send an old token with them
const CODE_LOGIN_PUBLIC_ENDPOINTS = ["loginMethod", "loginCodeRequest", "loginCodeVerify"];

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    credentials: "include",
    prepareHeaders: (headers, { endpoint }) => {
      const token = getAccessToken("accessToken");
      if (token && endpoint !== "login") {
        headers.set("Authorization", `Bearer ${token}`);
      }
      if (CODE_LOGIN_PUBLIC_ENDPOINTS.includes(endpoint)) {
        headers.delete("Authorization");
      }
      return headers;
    },
  }),

  endpoints: (builder) => ({
    authMe: builder.query({
      query: () => ({
        url: "/auth-me",
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (error) {
          console.error(error);
          localStorage.removeItem("accessToken");
        }
      },
    }),

    login: builder.mutation({
      query: (payload) => ({
        url: "/login",
        method: "POST",
        body: {
          email: payload.email,
          password: payload.password,
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const keepLogin = args.keepLogin;
          if (data.accessToken) {
            if (keepLogin) {
              localStorage.setItem("accessToken", data.accessToken);
            } else {
              sessionStorage.setItem("accessToken", data.accessToken);
            }
            dispatch(setUser(data.user));
            dispatch(setIsKeepLogin(keepLogin));
          }
        } catch (error) {
          console.error(error);
        }
      },
    }),

    createFirstLogin: builder.mutation({
      query: (payload) => ({
        url: "/create-first-password",
        method: "POST",
        body: payload,
      }),
    }),

    forgotPassword: builder.mutation({
      query: ({ email }) => ({
        url: "/forgot-password",
        method: "POST",
        body: {
          email: email,
        },
      }),
    }),

    verifyForgotPassword: builder.mutation({
      query: ({ code }) => ({
        url: "/verify-forgot-password",
        method: "POST",
        body: {
          code: code,
        },
      }),
    }),

    createNewPassword: builder.mutation({
      query: (payload) => ({
        url: `/create-new-password`,
        method: "POST",
        body: {
          newPassword: payload.new_password,
          userEmail: payload?.user?.email || payload.email || getAccessToken("email"),
          accessToken: getAccessToken("accessToken"),
        },
      }),
    }),

    createNewPasswordCheckId: builder.query({
      query: (payload) => ({
        url: `/create-new-password/${payload.id}`,
        method: "GET",
      }),
    }),

    emailUpdateRequest: builder.mutation({
      query: ({ user_id, email }) => ({
        url: "/email-update-request",
        method: "POST",
        body: {
          user_id,
          email,
          access_token: getAccessToken("accessToken"),
        },
      }),
    }),

    emailUpdateVerify: builder.mutation({
      query: ({ email }) => ({
        url: "/email-update-verify",
        method: "POST",
        body: {
          email,
          access_token: getAccessToken("accessToken"),
        },
      }),
    }),

    verifyEmailCode: builder.mutation({
      query: (payload) => ({
        url: `/verify-email-code/`,
        method: "POST",
        body: {
          access_token: getAccessToken("accessToken"),
          code: payload.code,
          new_email: payload.new_email,
          processor: payload.processor,
        },
      }),
    }),

    passwordUpdateRequest: builder.mutation({
      query: (payload) => ({
        url: "/password-update-request",
        method: "POST",
        body: {
          email: payload.email,
          isCodeVerification: payload.isCodeVerification || false,
          access_token: getAccessToken("accessToken"),
        },
      }),
    }),

    verifyPasswordConfirmationCode: builder.mutation({
      query: (payload) => ({
        url: "/verify-password-code",
        method: "POST",
        body: {
          code: payload.code,
          access_token: getAccessToken("accessToken"),
        },
      }),
    }),

    updateUserPassword: builder.mutation({
      query: (payload) => ({
        url: "/update-password",
        method: "POST",
        body: {
          new_password: payload.new_password,
          access_token: getAccessToken("accessToken"),
        },
      }),
    }),

    updateSubscription: builder.mutation({
      query: (payload) => ({
        url: "/pause-subscription",
        method: "POST",
        body: {
          subscription_id: payload.subscription_id,
          status: payload.status,
          access_token: getAccessToken("accessToken"),
        },
      }),
    }),

    cancelSubscription: builder.mutation({
      query: (payload) => ({
        url: "/cancel-subscription",
        method: "POST",
        body: {
          subscription_id: payload.subscription_id,
          access_token: getAccessToken("accessToken"),
        },
      }),
    }),

    // Email-first login: tells if the account signs in with an email code or a password
    loginMethod: builder.mutation<LoginMethodResponse, { email: string }>({
      query: ({ email }) => ({
        url: "/login-method",
        method: "POST",
        body: {
          email,
        },
      }),
    }),

    loginCodeRequest: builder.mutation<LoginCodeRequestResponse, { email: string }>({
      query: ({ email }) => ({
        url: "/login-code/request",
        method: "POST",
        body: {
          email,
        },
      }),
    }),

    // Stores the token exactly like "login" does, plus the code-login session keys
    loginCodeVerify: builder.mutation<LoginCodeVerifyResponse, LoginCodeVerifyPayload>({
      query: (payload) => ({
        url: "/login-code/verify",
        method: "POST",
        body: {
          email: payload.email,
          code: payload.code,
          loginTicket: payload.loginTicket,
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const keepLogin = args.keepLogin;
          if (data.accessToken) {
            if (keepLogin) {
              localStorage.setItem("accessToken", data.accessToken);
            } else {
              sessionStorage.setItem("accessToken", data.accessToken);
            }
            saveCodeLogin(keepLogin, data.session?.id, data.session?.secret);
            dispatch(setUser(data.user));
            dispatch(setIsKeepLogin(keepLogin));
          }
        } catch (error) {
          console.error(error);
        }
      },
    }),

    // Email change for accounts signed in with an email code (codes go to the old and the new address)
    emailChangeStart: builder.mutation<EmailChangeStartResponse, { newEmail: string }>({
      query: ({ newEmail }) => ({
        url: "/email-change/start",
        method: "POST",
        body: {
          api_token: getAccessToken("accessToken"),
          newEmail,
        },
      }),
    }),

    emailChangeConfirm: builder.mutation<EmailChangeConfirmResponse, { codeOld: string; codeNew: string }>({
      query: ({ codeOld, codeNew }) => ({
        url: "/email-change/confirm",
        method: "POST",
        body: {
          api_token: getAccessToken("accessToken"),
          codeOld,
          codeNew,
        },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useAuthMeQuery,
  useCreateFirstLoginMutation,
  useForgotPasswordMutation,
  useVerifyForgotPasswordMutation,
  useCreateNewPasswordMutation,
  useCreateNewPasswordCheckIdQuery,
  useEmailUpdateRequestMutation,
  useVerifyEmailCodeMutation,
  useEmailUpdateVerifyMutation,
  usePasswordUpdateRequestMutation,
  useVerifyPasswordConfirmationCodeMutation,
  useUpdateUserPasswordMutation,
  useUpdateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useLoginMethodMutation,
  useLoginCodeRequestMutation,
  useLoginCodeVerifyMutation,
  useEmailChangeStartMutation,
  useEmailChangeConfirmMutation,
} = authApi;
